import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { CreateStoreResponse, StoreKind } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import {
  invalidateAdminSellers,
  invalidateAdminStores,
} from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type CreateSellerStoreInput = {
  sellerId: string;
  storeName: string;
  /** Optional Latin store link (R2-D5); omitted ⇒ derived from the name. */
  storeSlug?: string;
  storeKind?: StoreKind;
  maxProductImageCount?: number;
  // Premium stores skip the subscription: when true, subscriptionPlanId is omitted.
  isPremium?: boolean;
  subscriptionPlanId?: string;
  subscriptionStartDate?: string;
  subscriptionOverrideAmount?: number;
  subscriptionOverridePeriodDays?: number;
};

export function useCreateSellerStoreMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ sellerId, ...body }: CreateSellerStoreInput) =>
      apiJsonClient<CreateStoreResponse>(
        endpoints.admin.sellerStores(sellerId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateAdminSellers(queryClient),
        invalidateAdminStores(queryClient),
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.sellerById(variables.sellerId),
          exact: true,
        }),
      ]);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
